<template>
  <div>
    <app-cells>
      <h2 class="title">Supplier</h2>
      <app-button @click.native="onSaveSupplier" ref="submitSupplier" size="small">Save</app-button>
    </app-cells>
    <app-flex-grid :rows="[['250px', '250px']]" align-items="start">
      <template #col-1-1>
        <app-form-field label="Supplier" required>
          <app-select
            @search="onSupplierSearch"
            @input="onSupplierSelected"
            v-model="form_supplier.supplier"
            :options="supplier_options"
            :clearable="false"
            :display-field="option => `${option.trade_name} ${option.tin ? `(TIN: ${option.tin})` : ''}`"
            label="trade_name"
            placeholder="Supplier"
            :error="$v.form_supplier.supplier.$error"
          />
          <template #error>
            <div v-if="$v.form_supplier.supplier.$dirty && !$v.form_supplier.supplier.required">Required field</div>
          </template>
        </app-form-field>
      </template>
      <template #col-1-2>
        <app-form-field label="Payment method" required>
          <app-select
            v-model="form_supplier.supplier_payment_method"
            :options="form_supplier.supplier?.payment_methods"
            label="public_name"
            :clearable="false"
            :searchable="false"
            :readonly="!form_supplier.supplier"
            placeholder="Payment method"
            :error="$v.form_supplier.supplier_payment_method.$error"
          />
          <template #error>
            <div v-if="$v.form_supplier.supplier_payment_method.$dirty && !$v.form_supplier.supplier_payment_method.required">Required field</div>
          </template>
        </app-form-field>
      </template>
    </app-flex-grid>
    <app-cells>
      <h2 class="title">Emails</h2>
      <app-button @click.native="onShowMailModal" size="small">Add email</app-button>
    </app-cells>
    <v-client-table
      :data="table.items"
      :columns="table.columns"
      :options="table.options"
      class="table-default table-default--40"
    >
      <template #controls="{ row }">
        <div class="table-default__controls">
          <button @click="onShowDeleteModal(row.id)" type="button">
            <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
          </button>
        </div>
      </template>
    </v-client-table>
    <modal
      name="mail-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideMailModal" class="popup__close" type="button"></button>
        <div class="popup__title">Add email</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Type" required>
              <app-select
                v-model="form.type"
                :clearable="false"
                :options="email_types_options"
                :reduce="item => item.code"
                placeholder="Type"
                :error="$v.form.type.$error"
              />
              <template #error>
                <div v-if="$v.form.type.$dirty && !$v.form.type.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Email" label-for="email" required>
              <app-input
                v-model="form.email"
                id="email"
                placeholder="Email"
                name="email"
                :error="$v.form.email.$error"
              />
              <template #error>
                <div v-if="$v.form.email.$dirty && !$v.form.email.required">Required field</div>
                <div v-if="$v.form.email.$dirty && !$v.form.email.email">Enter the correct e-mail format</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submitMail" :disabled="$v.form.$error">Add</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="Warning">
          Delete email
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this email?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteEmail" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import {
  getAdministrativeSuppliers,
  getReservationsEmailTypes,
  postDomesticFlightsAirlinesEmails,
  getDomesticFlightsAirlinesEmails,
  deleteDomesticFlightsAirlinesEmails,
  patchDomesticFlightsAirlinesDetail,
} from '@/http'
import { required, email } from 'vuelidate/lib/validators'

export default {
  name: 'AirlinesDatabaseDetail',
  props: {
    airline: {
      type: Object,
    },
  },
  data() {
    return {
      supplier_options: [],
      supplier_payment_method_options: [],
      email_types_options: [],
      form: {},
      form_supplier: {},
      email_to_action: null,
      table: {
        items: [],
        columns: ['type.name', 'email', 'controls'],
        options: {
          headings: {
            'type.name': 'Type',
            'email': 'Email',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  validations: {
    form: {
      email: {required, email},
      type: {required}
    },
    form_supplier: {
      supplier: {required},
      supplier_payment_method: {required},
    }
  },
  created() {
    getReservationsEmailTypes().then(response => { this.email_types_options = response.data })
    this.fetchEmails()
    this.form_supplier = {
      supplier: this.airline.supplier,
      supplier_payment_method: this.airline.supplier_payment_method,
    }
  },
  methods: {
    fetchEmails() {
      getDomesticFlightsAirlinesEmails(Number(this.$route.params.id)).then(response => {
        this.table.items = response.data
      })
    },
    onSaveSupplier() {
      this.$v.form_supplier.$touch()
      if (this.$v.form_supplier.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.$refs.submitSupplier.preload = true
        patchDomesticFlightsAirlinesDetail(
          Number(this.$route.params.id),
          {
            supplier: this.form_supplier.supplier.id,
            supplier_payment_method: this.form_supplier.supplier_payment_method.id,
          }
        )
          .then(() => {
            this.$emit('update')
            this.$notify({
              type: 'success',
              title: 'Data saved successfully',
            })
            this.$refs.submitSupplier.preload = false
          })
          .catch(() => {
            this.$refs.submitSupplier.preload = false
          })
      }
    },
    onSupplierSelected() {
      this.supplier_payment_method = null
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submitMail.preload = true
      postDomesticFlightsAirlinesEmails(Number(this.$route.params.id), {...this.form, airline: this.$route.params.id})
        .then(() => {
          this.fetchEmails()
          this.$notify({
            type: 'success',
            title: 'Data saved successfully',
          })
          this.onHideMailModal()
        })
        .catch(() => { this.$refs.submitMail.preload = false })
    },
    onShowMailModal() {
      this.form = {}
      this.$v.form.$reset()
      this.$modal.show('mail-modal')
    },
    onHideMailModal() {
      this.$modal.hide('mail-modal')
    },
    onShowDeleteModal(id) {
      this.email_to_action = id
      this.$modal.show('delete-modal')
    },
    onDeleteEmail() {
      this.$refs.delete.preload = true
      deleteDomesticFlightsAirlinesEmails(this.$route.params.id, this.email_to_action)
        .then(() => {
          this.fetchEmails()
          this.$notify({
            type: 'success',
            title: 'Email deleted',
          })
          this.$modal.hide('delete-modal')
        })
        .catch(() => { this.$refs.delete.preload = false })
    },
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
